import React, { useEffect, useState } from 'react';
import { Box, Chip, CircularProgress, Paper, Typography } from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {
  getUserEngagementSummary,
  type EngagementSummary,
} from '../../services/engagementApi';
import { formatDurationMs } from '../../services/adminAnalyticsApi';
import DocumentChip from '../DocumentChip';
import { parseDomainFromCode, titleFromDocumentCode } from '../../utils/documentCodeUtils';
import { useLanguage } from '../../hooks/useLanguage';

type Props = {
  userId: string;
};

export const UserEngagementInsights: React.FC<Props> = ({ userId }) => {
  const { navigateWithLang } = useLanguage();
  const [data, setData] = useState<EngagementSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getUserEngagementSummary(userId)
      .then((res) => {
        if (!cancelled && res?.data) setData(res.data);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (!data) return null;

  const { tracking, exercises, progression } = data;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" color="primary" fontWeight={600} gutterBottom>
        Learning activity (unified)
      </Typography>
      <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
        <Chip
          size="small"
          icon={<MenuBookIcon />}
          label={`${tracking.uniqueDocumentsViewed} docs viewed`}
          variant="outlined"
        />
        <Chip
          size="small"
          icon={<AccessTimeIcon />}
          label={formatDurationMs(tracking.totalDocumentTimeMs)}
          variant="outlined"
        />
        <Chip
          size="small"
          icon={<FitnessCenterIcon />}
          label={`${exercises.passRate}% exercise pass rate`}
          variant="outlined"
        />
        <Chip
          size="small"
          label={`${progression.completedDocuments}/${progression.totalDocuments} validated`}
          variant="outlined"
        />
      </Box>
      {tracking.mostViewedDocuments.length > 0 ? (
        <Box mt={1}>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Top docs
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.75}>
            {tracking.mostViewedDocuments.slice(0, 3).map((d) => {
              const code = d.documentCode || '';
              const title = d.documentTitle || titleFromDocumentCode(code);
              const domain = d.documentDomain || parseDomainFromCode(code);
              return (
                <DocumentChip
                  key={d.documentId}
                  document={{
                    id: d.documentId,
                    title,
                    code: code || undefined,
                    domain,
                  }}
                  onClick={
                    code
                      ? () =>
                          navigateWithLang(`/page-editor?q=${encodeURIComponent(code)}`)
                      : undefined
                  }
                />
              );
            })}
          </Box>
        </Box>
      ) : null}
    </Paper>
  );
};

export default UserEngagementInsights;
